import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HCHeatmap from 'highcharts/modules/heatmap';

HCHeatmap(Highcharts);

interface HeatmapProps {
  title: string;
  years: string[];
  hazards: string[];
  data: number[][];
  exporting: boolean;
}

const HAZARD_LABELS: Record<string, string> = {
  heat: 'Heat',
  drought: 'Drought',
  pluvial_flood_rain: 'Pluvial Flood',
};

export function Heatmap({ title, years, hazards, data, exporting }: HeatmapProps) {
  const mapValue = (v: number) => {
    if (v <= 0) return 0;
    if (v <= 0.01) return 0 + ((v - 0) / (0.01 - 0)) * 1;
    if (v <= 0.5) return 1 + ((v - 0.01) / (0.5 - 0.01)) * 1;
    if (v <= 5.0) return 2 + ((v - 0.5) / (5.0 - 0.5)) * 1;
    if (v <= 25.0) return 3 + ((v - 5.0) / (25.0 - 5.0)) * 1;
    return Math.min(5, 4 + ((v - 25.0) / 25.0) * 1);
  };

  const transformedData = data.flatMap((row, i) =>
    row.map((value, j) => ({
      x: j,
      y: i,
      value: mapValue(value),
      custom: { realValue: value },
    }))
  );

  const options: Highcharts.Options = {
    chart: {
      type: 'heatmap',
      height: 320,
    },
    title: { text: title },
    xAxis: { categories: years },
    yAxis: { categories: hazards.map((h) => HAZARD_LABELS[h] || h), title: undefined },
    exporting: { enabled: exporting },
    colorAxis: {
      min: 0,
      max: 5,
      stops: [
        [0, '#2f6f80'], // No risk start
        [0.2, '#87a18f'], // Negligible start (at 1)
        [0.4, '#d6b36a'], // Moderate start (at 2)
        [0.6, '#d47346'], // Significant start (at 3)
        [0.8, '#c92525'], // Severe start (at 4)
        [1, '#c92525'], // Severe end (at 5)
      ],
      labels: {
        formatter: function () {
          const value = this.value as number;
          // Map the 0-5 scale back to labels
          if (value === 0) return `0<br/>No risk`;
          if (value === 1) return `0.01<br/>Negligible`;
          if (value === 2) return `0.5<br/>Moderate`;
          if (value === 3) return `5.0<br/>Significant`;
          if (value === 4) return `25.0<br/>Severe`;
          return '';
        },
        useHTML: true,
        style: {
          fontSize: '11px',
          fontWeight: '600',
          color: '#333',
          textAlign: 'left',
        },
        x: 5,
      },
      tickPositions: [0, 1, 2, 3, 4],
      tickLength: 0,
      visible: true,
      showInLegend: true,
    },
    legend: {
      align: 'right',
      layout: 'vertical',
      verticalAlign: 'middle',
    },
    tooltip: {
      formatter: function () {
        // @ts-ignore
        const realValue = this.point.custom?.realValue;
        // @ts-ignore
        return `<b>${this.series.xAxis.categories[this.point.x]}</b><br/><b>${this.series.yAxis.categories[this.point.y]}</b>: ${realValue?.toFixed(2)}`;
      },
    },
    series: [
      {
        type: 'heatmap',
        borderWidth: 1,
        dataLabels: {
          enabled: true,
          color: '#000',
          formatter() {
            // @ts-ignore
            return this.point.custom?.realValue?.toFixed(1);
          },
        },
        data: transformedData,
      },
    ],
  };

  return <HighchartsReact highcharts={Highcharts} options={options} immutable={true} />;
}
